// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/DimensionsSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, theme } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DimensionsSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const dm = data?.dimensions;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = dm && (dm.height || dm.width || dm.depth || dm.length || dm.weight || dm.netWeight || dm.grossWeight);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const formatDimension = (value, unit) => {
    if (value === undefined || value === null) return '-';
    return `${value} ${unit || 'cm'}`;
  };

  const formatWeight = (value, unit) => {
    if (value === undefined || value === null) return '-';
    return `${value} ${unit || 'kg'}`;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['dimensions']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'dimensions',
          label: (
            <Flex align="center" gap={8}>
              <ExpandOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('dimensions')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {dm.height !== undefined && (
                <Descriptions.Item label={translateX('height')}>
                  {formatDimension(dm.height, dm.unit)}
                </Descriptions.Item>
              )}
              {dm.width !== undefined && (
                <Descriptions.Item label={translateX('width')}>
                  {formatDimension(dm.width, dm.unit)}
                </Descriptions.Item>
              )}
              {(dm.depth !== undefined || dm.length !== undefined) && (
                <Descriptions.Item label={translateX('length')}>
                  {formatDimension(dm.depth ?? dm.length, dm.unit)}
                </Descriptions.Item>
              )}
              {(dm.netWeight !== undefined || dm.weight !== undefined) && (
                <Descriptions.Item label={translateX('net_weight')}>
                  {formatWeight(dm.netWeight ?? dm.weight)}
                </Descriptions.Item>
              )}
              {dm.grossWeight !== undefined && (
                <Descriptions.Item label={translateX('gross_weight')}>
                  {formatWeight(dm.grossWeight)}
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DimensionsSection;
